import { useEffect, useMemo, useState } from 'react';
import { Text } from '@belong/ui';
import { format } from 'date-fns';
import { clsx } from 'clsx';
import { IconDots } from '@belong/icons';
import { PAYMENT_SCREENS } from 'containercomponents/Modals/PaymentsModal/PaymentsModal';
import PaymentMenuSelectionContainer from 'containers/PaymentSelectionMenuContainer/PaymentMenuSelectionContainer';
import { PaymentPreference } from 'models/enums';
import { FEEDBACK_STRINGS } from 'strings/errors.string';
import type {
  ConsumerPaymentMethodSummaryModel,
  ConsumerPaymentRescheduleModel,
  ConsumerPaymentRescheduleTotalChargeModel,
} from '../failed-payments.types';
import type { PaymentAccountResourceModel } from 'api/models';
import type { FailedPaymentsAudience } from '../failed-payments-audience';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatLateFee,
  mapPaymentAccountToPaymentMethodSummary,
} from '../failed-payments-utils';
import { PaymentMethodSummary } from '../payment-method-summary';

const DAYS_AHEAD = 6;

type ApiClient = {
  post: <T>(url: string, body: unknown) => Promise<T>;
};

type ScheduleLaterSubsectionProps = {
  apiClient: ApiClient;
  audience: FailedPaymentsAudience;
  balanceUniqueIds: string[];
  paymentMethods: PaymentAccountResourceModel[];
  paymentAccountUniqueId: string | null;
  paymentMethod: ConsumerPaymentMethodSummaryModel | null | undefined;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onSelectPaymentMethod: (id: string) => void;
  onLateFeeErrorChange: (hasError: boolean) => void;
};

export function ScheduleLaterSubsection({
  apiClient,
  audience,
  balanceUniqueIds,
  paymentMethods,
  paymentAccountUniqueId,
  paymentMethod,
  selectedDate,
  onSelectDate,
  onSelectPaymentMethod,
  onLateFeeErrorChange,
}: ScheduleLaterSubsectionProps) {
  const dates = useMemo(() => {
    const arr: { iso: string; label: string }[] = [];
    const base = new Date();
    for (let i = 1; i <= DAYS_AHEAD; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      arr.push({
        iso: format(d, 'yyyy-MM-dd'),
        label: format(d, 'EEE, MMM d, yyyy'),
      });
    }
    return arr;
  }, []);

  const [dateFees, setDateFees] = useState<Record<string, number>>({});
  const [lateFeeError, setLateFeeError] = useState(false);
  const datesKey = dates.map((d) => d.iso).join(',');
  const balancesKey = balanceUniqueIds.join(',');

  useEffect(() => {
    if (!paymentAccountUniqueId || balanceUniqueIds.length === 0) {
      setDateFees({});
      setLateFeeError(false);
      onLateFeeErrorChange(false);
      return;
    }
    let cancelled = false;

    (async () => {
      const results = await Promise.all(
        dates.map(async ({ iso }) => {
          try {
            const totals: ConsumerPaymentRescheduleTotalChargeModel[] =
              await Promise.all(
                balanceUniqueIds.map((id) =>
                  apiClient.post<ConsumerPaymentRescheduleTotalChargeModel>(
                    `/v2/users/balances/${id}/payment-reschedule/total`,
                    {
                      retryStrategy: 'ScheduleForLater',
                      scheduledRetryDate: iso,
                      paymentAccountUniqueId,
                    } as ConsumerPaymentRescheduleModel,
                  ),
                ),
              );
            const lateFee = totals.reduce(
              (sum, t) => sum + (t?.lateFeeCharge ?? 0),
              0,
            );
            return { iso, lateFee, hasError: false };
          } catch {
            return { iso, lateFee: 0, hasError: true };
          }
        }),
      );
      if (cancelled) return;
      const hasError = results.some((result) => result.hasError);
      const entries = results.map(
        ({ iso, lateFee }) => [iso, lateFee] as const,
      );
      setDateFees(Object.fromEntries(entries));
      setLateFeeError(hasError);
      onLateFeeErrorChange(hasError);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    apiClient,
    balancesKey,
    paymentAccountUniqueId,
    datesKey,
    onLateFeeErrorChange,
  ]);

  const availablePaymentMethods = paymentMethods.filter(
    (method) => method.accountId && method.isVerified,
  );
  const selectedFee = selectedDate ? (dateFees[selectedDate] ?? 0) : 0;
  const formattedSelectedFee = formatLateFee(selectedFee);
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (method) => method.accountId === paymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : paymentMethod;

  useEffect(() => {
    if (!paymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    onSelectPaymentMethod,
    paymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  const paymentModalProps = {
    screen: PAYMENT_SCREENS.PAYMENT_SELECTION,
    errorScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.error.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.error.ctaLabel,
    },
    successScreenProps: {
      title: FEEDBACK_STRINGS.set_payment_method.success.title,
      ctaLabel: FEEDBACK_STRINGS.set_payment_method.success.ctaLabel,
    },
  };

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <Text
          variant="h3"
          fontWeight="semibold"
          className="whitespace-pre-line"
        >
          {FAILED_PAYMENTS_STRINGS['schedule_later.prompt']}
        </Text>

        {selectedPaymentMethodSummary && (
          <div className="w-full">
            <PaymentMenuSelectionContainer
              selectedPaymentMethodId={paymentAccountUniqueId ?? ''}
              onSelectPaymentMethod={onSelectPaymentMethod}
              paymentType={PaymentPreference.Rent}
              paymentModalProps={paymentModalProps}
              showOnlyValidPaymentMethods
              shouldShowAutoSplitPaymentMethod={false}
              onSelectAutoSplitPaymentMethod={() => {}}
              fitToButton
              onTop
              menuButtonLabel={
                <span className="flex h-[106px] w-full items-center rounded border border-solid border-gray bg-white px-[17px] py-[34px] text-left">
                  <span className="min-w-0 flex-1">
                    <PaymentMethodSummary
                      paymentMethod={selectedPaymentMethodSummary}
                    />
                  </span>
                  <span className="inline-flex px-xs text-navy">
                    <IconDots width={5} height={24} />
                  </span>
                </span>
              }
            />
          </div>
        )}

        <div
          className={clsx(
            'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
            audience.key === 'homeowner' ? 'gap-sm' : 'gap-md',
          )}
        >
          {dates.map(({ iso, label }) => {
            const isSelected = selectedDate === iso;
            const fee = dateFees[iso] ?? 0;
            const formattedFee = formatLateFee(fee);
            return (
              <button
                key={iso}
                type="button"
                onClick={() => onSelectDate(iso)}
                className={clsx(
                  'flex h-[64px] w-full cursor-pointer flex-col items-center justify-center rounded border-0 px-sm shadow transition-colors',
                  isSelected ? 'bg-navy' : 'bg-white',
                )}
              >
                <Text
                  variant="body"
                  fontWeight={isSelected ? 'semibold' : 'regular'}
                  className={isSelected ? 'text-white' : 'text-navy'}
                >
                  {label}
                </Text>
                {fee > 0 && (
                  <Text
                    variant="p2"
                    fontWeight="semibold"
                    className="text-dark-gray"
                  >
                    + {formattedFee}{' '}
                    {FAILED_PAYMENTS_STRINGS['schedule_later.late_fee_label']}
                  </Text>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {lateFeeError && (
        <div className="flex flex-col gap-xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-md">
          <Text variant="p2" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['error.late_fee_preview']}
          </Text>
        </div>
      )}

      {selectedFee > 0 && (
        <div className="flex flex-col gap-xs rounded border border-solid border-green bg-green-translucent-light p-md">
          <Text fontWeight="semibold" className="text-navy">
            ⚠️ {formattedSelectedFee}{' '}
            {FAILED_PAYMENTS_STRINGS['schedule_later.late_fee_label']}
          </Text>
          <Text variant="p1" className="text-navy">
            {FAILED_PAYMENTS_STRINGS[audience.lateFeeDescriptionKey]}
          </Text>
        </div>
      )}
    </div>
  );
}
